import { Contact, HouseholdUpdatePayload } from "@/schema"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { useApiContext } from "@/hooks/api/use-api-context"

import {
  applySuggestedField,
  bulkConvertToContacts,
  bulkMoveHouseholdsToBook,
  BulkMoveHouseholdsToBookParams,
  convertToContact,
  createFamilyMemberFeedback,
  deleteFamilyMember,
  destroyHousehold,
  dismissSuggestedField,
  enrichHousehold,
  HouseholdEnrichmentResponse,
  mergeHousehold,
  MergeHouseholdParams,
  MergeHouseholdResponse,
  promoteToClient,
  rejectHouseholdEnrichment,
  updateHousehold,
  type HouseholdSuggestedFieldResponse,
} from "./api"

export function useUpdateHousehold(householdId: string) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: HouseholdUpdatePayload) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return updateHousehold(context, householdId, params)
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ["household", householdId] })
      queryClient.invalidateQueries({ queryKey: ["clients"] })
      queryClient.invalidateQueries({ queryKey: ["prospects"] })
      if (Object.hasOwn(params, "timezone")) {
        queryClient.invalidateQueries({ queryKey: ["tasks"] })
        queryClient.invalidateQueries({ queryKey: ["projects"] })
        queryClient.invalidateQueries({ queryKey: ["project"] })
        queryClient.invalidateQueries({ queryKey: ["household", householdId, "projects"] })
      }
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ["household", householdId] })
      queryClient.invalidateQueries({ queryKey: ["clients"] })
      queryClient.invalidateQueries({ queryKey: ["prospects"] })
    },
  })
}

export function useUpdateHouseholdReviewSettings() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      householdId,
      data,
    }: {
      householdId: string
      data: Pick<HouseholdUpdatePayload, "next_review_due_on" | "next_review_due_on_override" | "review_cadence">
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return updateHousehold(context, householdId, data)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["household", variables.householdId] })
      queryClient.invalidateQueries({ queryKey: ["clients"] })
      queryClient.invalidateQueries({ queryKey: ["prospects"] })
    },
  })
}

export function useUpdateHouseholdTouchpointSettings() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      householdId,
      data,
    }: {
      householdId: string
      data: Pick<
        HouseholdUpdatePayload,
        "next_touchpoint_due_on" | "next_touchpoint_due_on_override" | "touchpoint_cadence"
      >
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return updateHousehold(context, householdId, data)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["household", variables.householdId] })
      queryClient.invalidateQueries({ queryKey: ["clients"] })
      queryClient.invalidateQueries({ queryKey: ["prospects"] })
    },
  })
}

export function usePromoteToClient(prospectId: string, onSuccess?: () => void) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return promoteToClient(context, prospectId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["household", prospectId] })
      queryClient.invalidateQueries({ queryKey: ["clients"] })
      queryClient.invalidateQueries({ queryKey: ["prospects"] })
      if (onSuccess) onSuccess()
    },
  })
}

export function useConvertToContact(householdId: string, onSuccess?: (data: Contact) => void) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<Contact>({
    mutationFn: async (): Promise<Contact> => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return convertToContact(context, householdId)
    },
    onSuccess: (data) => {
      queryClient.removeQueries({ queryKey: ["household", householdId] })
      queryClient.invalidateQueries({ queryKey: ["households"] })
      queryClient.invalidateQueries({ queryKey: ["contacts"] })
      queryClient.invalidateQueries({ queryKey: ["clients"] })
      queryClient.invalidateQueries({ queryKey: ["prospects"] })
      if (onSuccess) onSuccess(data)
    },
  })
}

export function useBulkConvertToContacts(onSuccess?: () => void) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (householdIds: string[]) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return bulkConvertToContacts(context, householdIds)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["households"] })
      queryClient.invalidateQueries({ queryKey: ["contacts"] })
      queryClient.invalidateQueries({ queryKey: ["clients"] })
      queryClient.invalidateQueries({ queryKey: ["prospects"] })

      const { successful, failed } = data

      if (successful.length > 0) {
        toast.success(
          `Successfully converted ${successful.length} household${successful.length > 1 ? "s" : ""} to contact${successful.length > 1 ? "s" : ""}`
        )
      }

      if (failed.length > 0) {
        toast.error(`Failed to convert ${failed.length} household${failed.length > 1 ? "s" : ""}`)
      }

      if (onSuccess) onSuccess()
    },
  })
}

export function useDestroyHousehold() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (householdId: string) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return destroyHousehold(context, householdId)
    },
    meta: { errorToast: { fallback: "Failed to delete household" } },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["households"] })
      queryClient.invalidateQueries({ queryKey: ["clients"] })
      queryClient.invalidateQueries({ queryKey: ["prospects"] })
    },
  })
}

export function useDemoteToProspect(clientId: string, onSuccess?: () => void) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      const { demoteToProspect } = await import("@/hooks/api/clients/api")
      return demoteToProspect(context, clientId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["household", clientId] })
      queryClient.invalidateQueries({ queryKey: ["clients"] })
      queryClient.invalidateQueries({ queryKey: ["prospects"] })
      if (onSuccess) onSuccess()
    },
  })
}

export interface MergeHouseholdsParams extends MergeHouseholdParams {
  source_household_id: string
}

export function useMergeHousehold() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<MergeHouseholdResponse, Error, MergeHouseholdsParams>({
    mutationFn: async ({ source_household_id, ...params }: MergeHouseholdsParams) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return mergeHousehold(context, source_household_id, params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["household"] })
      queryClient.invalidateQueries({ queryKey: ["clients"] })
      queryClient.invalidateQueries({ queryKey: ["prospects"] })
    },
  })
}

export function useApplySuggestedField(householdId: string) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<HouseholdSuggestedFieldResponse, Error, string>({
    mutationFn: async (suggestedFieldId: string) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return applySuggestedField(context, householdId, suggestedFieldId)
    },
    onSuccess: () => {
      toast.success("Suggestion applied")
      queryClient.invalidateQueries({ queryKey: ["household", householdId] })
      queryClient.invalidateQueries({ queryKey: ["household", householdId, "person-data-enrichment"] })
    },
  })
}

export function useDismissSuggestedField(householdId: string) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<HouseholdSuggestedFieldResponse, Error, string>({
    mutationFn: async (suggestedFieldId: string) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return dismissSuggestedField(context, householdId, suggestedFieldId)
    },
    onSuccess: () => {
      toast.success("Suggestion dismissed")
      queryClient.invalidateQueries({ queryKey: ["household", householdId] })
      queryClient.invalidateQueries({ queryKey: ["household", householdId, "person-data-enrichment"] })
    },
  })
}

export function useEnrichProspect(householdId: string) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<HouseholdEnrichmentResponse>({
    mutationFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return enrichHousehold(context, householdId)
    },
    onSuccess: () => {
      toast.success("Enrichment requested")
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["household", householdId, "person-data-enrichment"] })
      queryClient.invalidateQueries({ queryKey: ["household", householdId, "suggested-fields"] })
      queryClient.invalidateQueries({ queryKey: ["household", householdId] })
    },
  })
}

export function useRejectHouseholdEnrichment(householdId: string) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return rejectHouseholdEnrichment(context, householdId)
    },
    onSuccess: () => {
      toast.success("Enriched data rejected")
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["household", householdId, "person-data-enrichment"] })
      queryClient.invalidateQueries({ queryKey: ["household", householdId, "suggested-fields"] })
      queryClient.invalidateQueries({ queryKey: ["household", householdId] })
    },
  })
}

export function useUploadHouseholdProfilePicture(
  householdId: string,
  options?: {
    onSuccess?: () => void
  }
) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (file: File) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return updateHousehold(context, householdId, { profile_picture: file })
    },
    onSuccess: () => {
      toast.success("Profile picture uploaded")
      queryClient.invalidateQueries({ queryKey: ["household", householdId] })
      queryClient.invalidateQueries({ queryKey: ["clients"] })
      queryClient.invalidateQueries({ queryKey: ["prospects"] })
      options?.onSuccess?.()
    },
  })
}

export function useRemoveHouseholdProfilePicture(householdId: string) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return updateHousehold(context, householdId, { profile_picture: null })
    },
    onSuccess: () => {
      toast.success("Profile picture removed")
      queryClient.invalidateQueries({ queryKey: ["household", householdId] })
      queryClient.invalidateQueries({ queryKey: ["clients"] })
      queryClient.invalidateQueries({ queryKey: ["prospects"] })
    },
  })
}

export function useBulkMoveHouseholdsToBook() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: BulkMoveHouseholdsToBookParams) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return bulkMoveHouseholdsToBook(context, params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["households"] })
      queryClient.invalidateQueries({ queryKey: ["clients"] })
      queryClient.invalidateQueries({ queryKey: ["prospects"] })
    },
  })
}

export function useCreateFamilyMemberFeedback(householdId: string) {
  const getContext = useApiContext()

  return useMutation({
    mutationFn: async ({
      memberId,
      sentiment,
      feedbackText,
    }: {
      memberId: string
      sentiment: "positive" | "negative"
      feedbackText?: string
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return createFamilyMemberFeedback(context, householdId, memberId, {
        sentiment,
        feedback_text: feedbackText,
      })
    },
  })
}

export function useDeleteFamilyMember(householdId: string) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (memberId: string) => {
      const context = await getContext()
      if (!context) throw new Error("No API context")
      return deleteFamilyMember(context, householdId, memberId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["household", householdId, "family-members"] })
    },
  })
}
